/**
 * Работа с кодами маркировки «Честный ЗНАК».
 *
 * Из 1С код приходит в человекочитаемом виде: «(01)04620477561987(21)5-Rk:%9DBO3GF».
 * Скобки здесь — только разметка, показывающая границы идентификаторов применения
 * (AI). В самом штрихкоде их нет: идентификаторы разделяются служебными символами
 * FNC1/GS, а данные идут сплошной строкой. Если закодировать строку вместе со
 * скобками, сканер прочитает их буквально и система маркировки такой код не найдёт.
 */

/**
 * Идентификаторы фиксированной длины: после них разделитель не нужен.
 * Длина указана без самого идентификатора. Список сокращён до тех AI,
 * что реально встречаются в кодах шин; для остальных считаем длину переменной —
 * это безопаснее, лишний разделитель код не испортит, а пропущенный испортит.
 */
const FIXED_LENGTH_AI = {
  '00': 18,
  '01': 14,
  '02': 14,
  11: 6,
  12: 6,
  13: 6,
  15: 6,
  17: 6,
  20: 2,
};

/**
 * Разбирает человекочитаемый код в список пар «идентификатор — значение».
 * Возвращает null, если строка не похожа на код маркировки.
 */
export function parseMarkingCode(code) {
  if (typeof code !== 'string' || !code.startsWith('(')) return null;

  const re = /\((\d{2,4})\)/g;
  const positions = [];
  let match;
  while ((match = re.exec(code)) !== null) {
    positions.push({ ai: match[1], start: match.index, end: re.lastIndex });
  }
  if (positions.length === 0) return null;

  const parts = positions.map((pos, i) => {
    const valueEnd = i + 1 < positions.length ? positions[i + 1].start : code.length;
    return { ai: pos.ai, value: code.slice(pos.end, valueEnd) };
  });

  return parts.every((p) => p.value.length > 0) ? parts : null;
}

/**
 * Собирает строку для кодирования в DataMatrix (в записи bwip-js).
 *
 * Первым идёт FNC1 — признак того, что код построен по правилам GS1. Между
 * полями переменной длины ставится разделитель GS; после последнего он не нужен.
 *
 * Возвращает null, если код разобрать не удалось: лучше показать ошибку,
 * чем нарисовать заведомо неверный штрихкод.
 */
export function toDataMatrixPayload(code) {
  const parts = parseMarkingCode(code);
  if (!parts) return null;

  let out = '^FNC1';
  parts.forEach((part, i) => {
    out += part.ai + part.value;

    const isLast = i === parts.length - 1;
    const fixedLength = FIXED_LENGTH_AI[part.ai];
    const isVariableLength = fixedLength === undefined || part.value.length !== fixedLength;

    if (!isLast && isVariableLength) {
      out += '^029'; // GS (0x1D) в записи bwip-js
    }
  });

  return out;
}

/** GTIN из кода — для подписи под штрихкодом. */
export function extractGtin(code) {
  return parseMarkingCode(code)?.find((p) => p.ai === '01')?.value ?? null;
}

/** Серийный номер из кода. */
export function extractSerial(code) {
  return parseMarkingCode(code)?.find((p) => p.ai === '21')?.value ?? null;
}
